import { useState, useEffect } from 'react';
import axios from 'axios';
import { API } from '@/App';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Plus } from 'lucide-react';
import { fmtDate } from './fleetUtils';
import AttachmentGallery from './AttachmentGallery';

const RESP = { a_determiner: 'À déterminer', responsable: 'Responsable', non_responsable: 'Non responsable', partagee: 'Partagée', attente_assurance: 'Attente assurance' };
const STATUSES = { brouillon: 'Brouillon', declare: 'Déclaré', documents_manquants: 'Documents manquants', analyse: 'En cours d\'analyse', declare_assurance: 'Déclaré à l\'assurance', attente_decision: 'En attente de décision', reparation_en_cours: 'Réparation en cours', clos: 'Clos' };

const AccidentForm = ({ vehicles, sites, open, onClose, onCreated }) => {
  const [step, setStep] = useState(1);
  const [f, setF] = useState({ vehicle_id: '', date: '', time: '', site_id: '', employee_user_id: '', employee_justification: '', location: '', description: '', accident_type: '', gravity: 'leger', damages: '', third_party: false, bodily_injury: false, police_involved: false, vehicle_usable: 'a_determiner', report_available: 'en_attente', responsibility: 'a_determiner' });
  const [employees, setEmployees] = useState([]);
  const [empSearch, setEmpSearch] = useState('');
  const [empSiteFilter, setEmpSiteFilter] = useState('');
  const [auto, setAuto] = useState(null);
  const [photoIds, setPhotoIds] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => { if (open) { setStep(1); setPhotoIds([]); } }, [open]);

  useEffect(() => {
    const target = empSiteFilter || f.site_id;
    if (!target) { setEmployees([]); return; }
    axios.get(`${API}/fleet/sites/${target}/employees?include_inactive=true`, { withCredentials: true })
      .then(r => setEmployees(r.data)).catch(() => setEmployees([]));
  }, [f.site_id, empSiteFilter]);

  useEffect(() => {
    if (!f.vehicle_id || !f.date) { setAuto(null); return; }
    axios.get(`${API}/fleet/accidents/context?vehicle_id=${f.vehicle_id}&date=${f.date}`, { withCredentials: true })
      .then(r => {
        setAuto(r.data.auto_selected);
        if (r.data.auto_selected && !f.employee_user_id) setF(p => ({ ...p, employee_user_id: r.data.auto_selected.user_id }));
      }).catch(() => setAuto(null));
  }, [f.vehicle_id, f.date]);

  const uploadDoc = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      fd.append('context', 'accident');
      const res = await axios.post(`${API}/fleet/photos`, fd, { withCredentials: true });
      setPhotoIds(p => [...p, res.data.photo_id]);
    } catch (e) { alert(e.response?.data?.detail || 'Échec envoi'); }
    finally { setUploading(false); }
  };

  const submit = async () => {
    setBusy(true);
    try {
      const res = await axios.post(`${API}/fleet/accidents`, { ...f, employee_user_id: f.employee_user_id || null, site_id: f.site_id || null, photo_ids: photoIds }, { withCredentials: true });
      alert(`Accident déclaré (${res.data.accident_id})`);
      onCreated?.(); onClose();
    } catch (e) { alert(e.response?.data?.detail || 'Erreur'); }
    finally { setBusy(false); }
  };

  if (!open) return null;
  const filteredEmp = employees.filter(e => {
    if (!empSearch) return true;
    const q = empSearch.toLowerCase();
    return (e.name || '').toLowerCase().includes(q) || (e.email || '').toLowerCase().includes(q);
  });
  const canNext1 = f.site_id && f.date && f.vehicle_id;
  const canNext2 = f.description.trim();
  const selEmp = employees.find(e => e.user_id === f.employee_user_id);
  const stepTitle = { 1: 'Étape 1/4 — Contexte', 2: 'Étape 2/4 — Problème', 3: 'Étape 3/4 — Documents', 4: 'Étape 4/4 — Résumé' };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="accident-form">
        <DialogHeader><DialogTitle>Déclarer un accident — {stepTitle[step]}</DialogTitle></DialogHeader>
        <div className="flex gap-1 mb-2">{[1, 2, 3, 4].map(s => <div key={s} className={`h-1.5 flex-1 rounded ${s <= step ? 'bg-blue-600' : 'bg-gray-200'}`} />)}</div>

        {step === 1 && (
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Site *</Label>
              <select className="w-full px-2 py-2 border rounded-md bg-white text-sm" value={f.site_id} onChange={(e) => setF({ ...f, site_id: e.target.value })} data-testid="acc-site">
                <option value="">Choisir</option>{sites.map(s => <option key={s.site_id} value={s.site_id}>{s.name}</option>)}
              </select></div>
            <div><Label>Véhicule *</Label>
              <select className="w-full px-2 py-2 border rounded-md bg-white text-sm" value={f.vehicle_id} onChange={(e) => setF({ ...f, vehicle_id: e.target.value })} data-testid="acc-vehicle">
                <option value="">Choisir</option>{vehicles.map(v => <option key={v.vehicle_id} value={v.vehicle_id}>{v.plate_number}</option>)}
              </select></div>
            <div><Label>Date *</Label><Input type="date" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="acc-date" /></div>
            <div><Label>Heure</Label><Input type="time" value={f.time} onChange={(e) => setF({ ...f, time: e.target.value })} /></div>
            <div className="col-span-2">
              <Label>Salarié concerné {auto && <span className="text-xs text-blue-600">(proposé automatiquement : {auto.name})</span>}</Label>
              <p className="text-xs text-gray-500 mb-1">La personne qui utilisait le véhicule — cela ne préjuge pas de sa responsabilité.</p>
              <div className="flex gap-2 mb-1">
                <Input placeholder="Rechercher par nom ou e-mail..." value={empSearch} onChange={(e) => setEmpSearch(e.target.value)} data-testid="acc-emp-search" />
                <select className="px-2 py-2 border rounded-md bg-white text-sm" value={empSiteFilter} onChange={(e) => setEmpSiteFilter(e.target.value)} data-testid="acc-emp-site-filter">
                  <option value="">Site sélectionné</option>
                  <option value="all">Tous les sites</option>
                  {sites.map(s => <option key={s.site_id} value={s.site_id}>{s.name}</option>)}
                </select>
              </div>
              <select className="w-full px-2 py-2 border rounded-md bg-white text-sm" value={f.employee_user_id} onChange={(e) => setF({ ...f, employee_user_id: e.target.value })} data-testid="acc-employee">
                <option value="">Salarié non identifié (à compléter plus tard)</option>
                {filteredEmp.map(e => <option key={e.user_id} value={e.user_id}>{e.name}{e.email ? ` — ${e.email}` : ''}{e.inactive ? ' [Inactif]' : ''}</option>)}
              </select>
              {selEmp?.inactive && <p className="text-xs text-orange-600 mt-1">⚠️ Salarié inactif — sélection possible uniquement pour un événement historique.</p>}
              {auto && f.employee_user_id && f.employee_user_id !== auto.user_id && (
                <Input className="mt-1" placeholder="Justification obligatoire (salarié différent de la proposition)" value={f.employee_justification}
                  onChange={(e) => setF({ ...f, employee_justification: e.target.value })} data-testid="acc-emp-justif" />
              )}
            </div>
            <div className="col-span-2"><Label>Lieu</Label><Input value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} /></div>
          </div>
        )}

        {step === 2 && (
          <div className="grid grid-cols-2 gap-3">
            <div><Label>Type d'accident</Label><Input value={f.accident_type} onChange={(e) => setF({ ...f, accident_type: e.target.value })} placeholder="collision, choc, etc." /></div>
            <div><Label>Gravité</Label>
              <select className="w-full px-2 py-2 border rounded-md bg-white text-sm" value={f.gravity} onChange={(e) => setF({ ...f, gravity: e.target.value })} data-testid="acc-gravity">
                <option value="leger">Léger</option><option value="moyen">Moyen</option><option value="grave">Grave</option><option value="critique">Critique</option>
              </select></div>
            <div className="col-span-2"><Label>Description *</Label><Textarea rows={3} value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="acc-description" /></div>
            <div className="col-span-2"><Label>Dégâts constatés</Label><Input value={f.damages} onChange={(e) => setF({ ...f, damages: e.target.value })} /></div>
            <div><Label>Véhicule utilisable</Label>
              <select className="w-full px-2 py-2 border rounded-md bg-white text-sm" value={f.vehicle_usable} onChange={(e) => setF({ ...f, vehicle_usable: e.target.value })} data-testid="acc-usable">
                <option value="a_determiner">À déterminer</option><option value="oui">Oui</option><option value="non">Non</option>
              </select>
              {f.vehicle_usable === 'non' && <p className="text-xs text-red-600 mt-1">Le chef de parc recevra une alerte « Décision d'immobilisation requise ».</p>}
            </div>
            <div className="col-span-2 flex flex-wrap gap-4 text-sm">
              <label className="flex items-center gap-1"><input type="checkbox" checked={f.third_party} onChange={(e) => setF({ ...f, third_party: e.target.checked })} data-testid="acc-third-party" /> Tiers impliqué</label>
              <label className="flex items-center gap-1"><input type="checkbox" checked={f.bodily_injury} onChange={(e) => setF({ ...f, bodily_injury: e.target.checked })} data-testid="acc-injury" /> Dommage corporel</label>
              <label className="flex items-center gap-1"><input type="checkbox" checked={f.police_involved} onChange={(e) => setF({ ...f, police_involved: e.target.checked })} /> Police / gendarmerie</label>
            </div>
          </div>
        )}

        {step === 3 && (
          <div className="space-y-3">
            {f.third_party && (
              <div><Label>Constat amiable</Label>
                <select className="w-full px-2 py-2 border rounded-md bg-white text-sm" value={f.report_available} onChange={(e) => setF({ ...f, report_available: e.target.value })} data-testid="acc-report">
                  <option value="en_attente">En attente</option><option value="disponible">Disponible (joint ci-dessous)</option><option value="indisponible">Indisponible</option>
                </select></div>
            )}
            <div>
              <Label>Photos et documents (constat PDF, rapport, justificatifs)</Label>
              <input type="file" accept="image/jpeg,image/png,image/webp,application/pdf" id="acc-doc-input" className="hidden"
                onChange={(e) => uploadDoc(e.target.files?.[0])} />
              <Button type="button" variant="outline" className="w-full mt-1" disabled={uploading}
                onClick={() => document.getElementById('acc-doc-input').click()} data-testid="acc-doc-upload">
                {uploading ? 'Envoi...' : `Ajouter un document (${photoIds.length} joint(s))`}
              </Button>
            </div>
          </div>
        )}

        {step === 4 && (
          <div className="text-sm space-y-1 bg-gray-50 border rounded p-3" data-testid="acc-summary">
            <p><strong>Véhicule :</strong> {vehicles.find(v => v.vehicle_id === f.vehicle_id)?.plate_number} · <strong>Site :</strong> {sites.find(s => s.site_id === f.site_id)?.name} · <strong>Date :</strong> {f.date} {f.time}</p>
            <p><strong>Salarié concerné :</strong> {selEmp?.name || auto?.name || 'Non identifié'}{selEmp?.inactive ? ' [Inactif]' : ''}</p>
            <p><strong>Gravité :</strong> {f.gravity} · <strong>Véhicule utilisable :</strong> {f.vehicle_usable} · <strong>Tiers :</strong> {f.third_party ? 'oui' : 'non'}</p>
            <p><strong>Description :</strong> {f.description}</p>
            <p><strong>Documents joints :</strong> {photoIds.length}</p>
            <p className="text-xs text-gray-500">La responsabilité reste « À déterminer » — elle sera évaluée ensuite par le chef de parc ou le Super Admin.</p>
          </div>
        )}

        <div className="flex justify-between pt-2">
          <Button variant="outline" disabled={step === 1} onClick={() => setStep(step - 1)} data-testid="acc-prev">Précédent</Button>
          {step < 4 ? (
            <Button onClick={() => setStep(step + 1)} disabled={(step === 1 && !canNext1) || (step === 2 && !canNext2)} data-testid="acc-next">Suivant</Button>
          ) : (
            <Button onClick={submit} disabled={busy} data-testid="acc-submit">Déclarer l'accident</Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export const AccidentDetail = ({ accidentId, open, onClose, onChanged }) => {
  const [data, setData] = useState(null);
  const [upd, setUpd] = useState({ status: '', responsibility: '', justification: '', comment: '', insurance_decision: '' });
  const load = () => axios.get(`${API}/fleet/accidents/${accidentId}`, { withCredentials: true }).then(r => setData(r.data)).catch(e => { alert(e.response?.data?.detail); onClose(); });
  useEffect(() => { if (open && accidentId) { setData(null); load(); } }, [open, accidentId]);
  if (!open || !data) return open ? <Dialog open onOpenChange={onClose}><DialogContent><p className="p-4 text-sm">Chargement...</p></DialogContent></Dialog> : null;
  const a = data.accident;
  const save = async () => {
    try {
      const body = {};
      if (upd.status && upd.status !== a.status) body.status = upd.status;
      if (upd.responsibility && upd.responsibility !== a.responsibility) { body.responsibility = upd.responsibility; body.justification = upd.justification; }
      if (upd.comment) body.comment = upd.comment;
      if (upd.insurance_decision) body.insurance_decision = upd.insurance_decision;
      await axios.put(`${API}/fleet/accidents/${accidentId}`, body, { withCredentials: true });
      setUpd({ status: '', responsibility: '', justification: '', comment: '', insurance_decision: '' });
      load(); onChanged?.();
    } catch (e) { alert(e.response?.data?.detail || 'Erreur'); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto" data-testid="accident-detail">
        <DialogHeader><DialogTitle>Accident {a.plate_at_accident} — {a.date}</DialogTitle></DialogHeader>
        <div className="text-sm space-y-1">
          <p><strong>Statut :</strong> {STATUSES[a.status]} · <strong>Gravité :</strong> {a.gravity} · <strong>Responsabilité :</strong> {RESP[a.responsibility]}</p>
          <p><strong>Salarié :</strong> {a.employee_name_at_accident || 'Non identifié'} · <strong>Site :</strong> {a.site_id || '—'} · <strong>Constat :</strong> {a.report_available}</p>
          <p><strong>Description :</strong> {a.description}</p>
          {a.insurance_decision && <p><strong>Décision assurance :</strong> {a.insurance_decision}</p>}
          <p className="text-xs text-gray-500">Déclaré par {a.created_by?.name} le {fmtDate(a.created_at)} · {(a.issues_created || []).length} anomalie(s) générée(s)</p>
        </div>
        {((a.documents || []).length > 0 || (a.photo_ids || []).length > 0) && (
          <div>
            <p className="text-sm font-semibold mb-1">Documents et photos ({(a.documents || []).length || (a.photo_ids || []).length})</p>
            <AttachmentGallery photoIds={(a.documents || []).map(d => d.photo_id).concat(a.photo_ids || [])} />
          </div>
        )}
        <div className="border rounded p-3 space-y-2 bg-blue-50">
          <div className="grid grid-cols-2 gap-2">
            <select className="px-2 py-2 border rounded-md bg-white text-sm" value={upd.status} onChange={(e) => setUpd({ ...upd, status: e.target.value })} data-testid="acc-status-select">
              <option value="">Statut...</option>{Object.entries(STATUSES).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
            <select className="px-2 py-2 border rounded-md bg-white text-sm" value={upd.responsibility} onChange={(e) => setUpd({ ...upd, responsibility: e.target.value })} data-testid="acc-resp-select">
              <option value="">Responsabilité...</option>{Object.entries(RESP).map(([k, l]) => <option key={k} value={k}>{l}</option>)}
            </select>
          </div>
          {upd.responsibility && <Input placeholder="Justification obligatoire (changement de responsabilité)" value={upd.justification} onChange={(e) => setUpd({ ...upd, justification: e.target.value })} data-testid="acc-resp-justif" />}
          <Input placeholder="Décision de l'assurance" value={upd.insurance_decision} onChange={(e) => setUpd({ ...upd, insurance_decision: e.target.value })} />
          <Textarea rows={2} placeholder="Commentaire" value={upd.comment} onChange={(e) => setUpd({ ...upd, comment: e.target.value })} />
          <Button size="sm" onClick={save} data-testid="acc-save-btn">Enregistrer</Button>
        </div>
        <div>
          <p className="text-sm font-semibold mb-1">Historique (immuable)</p>
          <div className="space-y-1 max-h-40 overflow-y-auto">
            {data.events.map(ev => (
              <div key={ev.event_id} className="text-xs border-l-2 border-blue-300 pl-2">
                <p className="font-medium">{ev.action} {ev.old_value || ev.new_value ? `(${ev.old_value || '—'} → ${ev.new_value || '—'})` : ''}</p>
                <p className="text-gray-500">{ev.actor?.name} — {fmtDate(ev.created_at)}{ev.comment ? ` — ${ev.comment}` : ''}</p>
              </div>
            ))}
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

const AccidentsTab = ({ vehicles, sites }) => {
  const [accidents, setAccidents] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [detailId, setDetailId] = useState(null);
  const load = () => axios.get(`${API}/fleet/accidents`, { withCredentials: true }).then(r => setAccidents(r.data)).catch(console.error);
  useEffect(() => { load(); }, []);
  return (
    <div className="space-y-4" data-testid="accidents-tab">
      <Button className="btn-primary" onClick={() => setShowForm(true)} data-testid="new-accident-btn"><Plus className="w-4 h-4 mr-1" /> Déclarer un accident</Button>
      <div className="space-y-2">
        {accidents.map(a => (
          <Card key={a.accident_id} className="cursor-pointer hover:border-blue-300" onClick={() => setDetailId(a.accident_id)} data-testid={`accident-row-${a.accident_id}`}>
            <CardContent className="p-3 flex flex-wrap items-center justify-between gap-2">
              <div>
                <p className="font-mono font-bold">{a.plate_at_accident} <span className="font-sans font-normal text-sm text-gray-600">— {a.date} {a.time || ''}</span></p>
                <p className="text-xs text-gray-500">{a.employee_name_at_accident || 'Salarié non identifié'} · {a.accident_type || 'accident'} · {RESP[a.responsibility]}</p>
              </div>
              <div className="flex gap-2">
                <span className={`text-xs px-2 py-1 rounded font-bold ${['grave', 'critique'].includes(a.gravity) ? 'bg-red-600 text-white' : 'bg-yellow-100 text-yellow-800'}`}>{a.gravity}</span>
                <span className="text-xs px-2 py-1 rounded bg-blue-100 text-blue-700">{STATUSES[a.status]}</span>
              </div>
            </CardContent>
          </Card>
        ))}
        {accidents.length === 0 && <p className="text-sm text-gray-400">Aucun accident déclaré</p>}
      </div>
      <AccidentForm vehicles={vehicles} sites={sites} open={showForm} onClose={() => setShowForm(false)} onCreated={load} />
      {detailId && <AccidentDetail accidentId={detailId} open onClose={() => setDetailId(null)} onChanged={load} />}
    </div>
  );
};

export default AccidentsTab;
